import useEntityMentionContext from '@providers/EntityMention/useEntityMentionContext';
import { type SuggestionProps } from '@tiptap/suggestion';
import Fuse from 'fuse.js';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import {
    type EntitySuggestionItem,
} from '../types';
import { EntitySelector, type EntitySelectorRef } from './EntitySelector';
import {
    EntitySuggestionList,
    type EntitySuggestionListRef,
} from './EntitySuggestionList';

export type MentionSuggestionWrapperRef = {
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

export const MentionSuggestionWrapper = forwardRef<
    MentionSuggestionWrapperRef,
    SuggestionProps<EntitySuggestionItem>
>((props, ref) => {
    const entitySelectorRef = useRef<EntitySelectorRef>(null);
    const entitySuggestionListRef = useRef<EntitySuggestionListRef>(null);

    const {
        selectedEntityType,
        searchQuery,
        genericSearchResults,
        entities,
        genericSearchLoading,
        availableEntityTypes,
        entityMeta,
    } = useEntityMentionContext((context) => context.state);

    const { setSelectedEntityType, setSearchQuery, setMentionSuggestionOpen } =
        useEntityMentionContext((context) => context.actions);

    const clearEditorQuery = useCallback(() => {
        // clear the typed query in the editor after the trigger '@'
        // Keep the trigger so the suggestion stays open with an empty query
        const from = props.range.from + 1; // skip '@'
        const to = props.range.to;
        if (to > from) {
            props.editor.chain().focus().deleteRange({ from, to }).run();
        }
    }, [props.editor, props.range.from, props.range.to]);

    const handleEntityTypeSelect = useCallback(
        (entityType: string) => {
            // Select entity type and clear search so entity list shows unfiltered
            setSelectedEntityType(entityType);
            setSearchQuery('');
            clearEditorQuery();
        },
        [setSelectedEntityType, setSearchQuery, clearEditorQuery],
    );

    const handleBack = useCallback(() => {
        setSelectedEntityType(null);
        setSearchQuery('');
        clearEditorQuery();
    }, [setSelectedEntityType, setSearchQuery, clearEditorQuery]);

    useEffect(() => {
        setSearchQuery(props.query);
    }, [props.query, setSearchQuery, selectedEntityType]);

    useEffect(() => {
        setMentionSuggestionOpen(true);

        return () => {
            setMentionSuggestionOpen(false);
        };
    }, [setMentionSuggestionOpen]);

    const shouldShowGenericSearch =
        !selectedEntityType &&
        searchQuery.trim() !== '' &&
        !availableEntityTypes.some((type) => {
            const meta = entityMeta[type];
            if (!meta) return false;
            return meta.label
                .toLowerCase()
                .includes(searchQuery.toLowerCase());
        });

    const itemsSource = useMemo((): EntitySuggestionItem[] => {
        if (shouldShowGenericSearch) {
            return genericSearchResults;
        }
        if (selectedEntityType) {
            const entityState = entities[selectedEntityType];
            return entityState?.data ?? [];
        }
        return [];
    }, [
        shouldShowGenericSearch,
        selectedEntityType,
        genericSearchResults,
        entities,
    ]);

    const getCurrentEntityItems = useMemo((): EntitySuggestionItem[] => {
        const query = searchQuery?.trim() ?? '';

        // If we already fetched server-ranked results for this entity type + query,
        // show them as-is to preserve fuzzy/AI matches.
        if (selectedEntityType && query) {
            return itemsSource;
        }

        if (!query) return itemsSource;

        const fuse = new Fuse(itemsSource, {
            keys: ['label'],
            threshold: 0.3,
            distance: 100,
            minMatchCharLength: 2,
            shouldSort: true,
            includeScore: true,
        });
        return fuse.search(query).map((res) => res.item);
    }, [itemsSource, searchQuery, selectedEntityType]);

    const isEntitySuggestionsLoading = useMemo(
        () =>
            (selectedEntityType
                ? entities[selectedEntityType]?.isLoading
                : false) || genericSearchLoading,
        [selectedEntityType, entities, genericSearchLoading],
    );

    useImperativeHandle(ref, () => ({
        onKeyDown: (keyProps) => {
            if (selectedEntityType || shouldShowGenericSearch) {
                // Delegate to entity suggestion list
                return (
                    entitySuggestionListRef.current?.onKeyDown(keyProps) ||
                    false
                );
            } else {
                // Delegate to entity selector
                return entitySelectorRef.current?.onKeyDown(keyProps) || false;
            }
        },
    }));

    if (selectedEntityType || shouldShowGenericSearch) {
        return (
            <EntitySuggestionList
                ref={entitySuggestionListRef}
                items={getCurrentEntityItems}
                command={props.command}
                query={searchQuery}
                selectedEntityType={selectedEntityType}
                onBack={selectedEntityType ? handleBack : undefined}
                isLoading={isEntitySuggestionsLoading}
                entityMeta={entityMeta}
            />
        );
    }

    return (
        <EntitySelector
            ref={entitySelectorRef}
            availableEntityTypes={availableEntityTypes}
            onEntityTypeSelect={handleEntityTypeSelect}
            query={searchQuery}
            entityMeta={entityMeta}
        />
    );
});

MentionSuggestionWrapper.displayName = 'MentionSuggestionWrapper';
